/**
 * EvaluationRatification.jsx — Phase 2C C5: Ratification Dashboard
 *
 * Acceptance rules enforced:
 *   1. Coordinator section: shows eligible cases + "Submit for Ratification" button.
 *   2. Ratifier section: sees frozen manifest (resolved marks + source) + approve button.
 *   3. SOD enforced: ratifier ≠ any marker/moderator in scope (server returns 409 + UI surfaces it).
 *   4. After ratification: records are immutable (locked).
 *   5. Case status pipeline: ELIGIBLE → READY_FOR_RATIFICATION → RATIFIED.
 *   6. Student access blocked via EvaluationShell.
 *   7. Ratified manifest members shown with resolved_mark_snapshot (immutable).
 *   8. RATIFICATION → Phase 1E handoff triggered automatically by service.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Lock,
  X,
  Info,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Send,
  ClipboardCheck,
  Gavel,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OFFERING_ID = "2c000040-2c00-2c00-2c00-2c0000000040";
const COMPONENT_ID = "2c000014-2c00-2c00-2c00-2c0000000014";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(body?.detail?.message || String(body?.detail || r.statusText)), { status: r.status, detail: body?.detail });
  return body;
}

const RATIFIER_ROLES = new Set(["dean", "institution_admin", "super_admin"]);
const COORD_ROLES   = new Set(["programme_manager", "super_admin", "institution_admin", "registrar", "exam_admin"]);

// ─── Case row ─────────────────────────────────────────────────────────────────

function CaseRow({ c, index }) {
  const STATUS_STYLE = {
    MARKED:                 "bg-sky-950 text-sky-300 border-sky-800/60",
    SECOND_MARKED:          "bg-teal-950 text-teal-300 border-teal-800/60",
    MODERATED:              "bg-violet-950 text-violet-300 border-violet-800/60",
    READY_FOR_RATIFICATION: "bg-amber-950 text-amber-300 border-amber-800/60",
    RATIFIED:               "bg-green-950 text-green-300 border-green-800/60",
  };
  const cls = STATUS_STYLE[c.status] || "bg-slate-800 text-slate-400 border-slate-700";

  return (
    <div
      data-testid={`case-row-${c.id}`}
      className="grid grid-cols-[1fr_110px_80px_80px_120px] gap-3 px-4 py-3 border-b border-slate-800/50 items-center last:border-b-0 text-sm"
    >
      <span className="text-xs text-slate-500 font-mono">{c.id?.slice(0, 16)}…</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls} w-fit`}>
        {c.status?.replace(/_/g, " ")}
      </span>
      <span className="text-center font-mono font-semibold text-slate-200">
        {c.resolved_mark ?? "—"}
      </span>
      <span className="text-center text-slate-500">/ {c.max_mark}</span>
      <span className="text-xs text-slate-600 text-right">
        {c.resolved_source?.replace(/_/g, " ") || "—"}
      </span>
    </div>
  );
}

// ─── Ratification record card ─────────────────────────────────────────────────

function RatificationRecordCard({ rec }) {
  const [expanded, setExpanded] = useState(false);

  const statusStyle = {
    RATIFIED: "bg-green-950 text-green-300 border-green-800/60",
    REJECTED: "bg-red-950 text-red-300 border-red-800/60",
    PENDING:  "bg-amber-950 text-amber-300 border-amber-800/60",
  }[rec.status] || "bg-slate-800 text-slate-400 border-slate-700";

  return (
    <div data-testid={`ratification-record-${rec.id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
          {rec.status === "RATIFIED" ? (
            <CheckCircle2 className="w-4 h-4 text-green-400" />
          ) : rec.status === "REJECTED" ? (
            <X className="w-4 h-4 text-red-400" />
          ) : (
            <Gavel className="w-4 h-4 text-amber-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusStyle}`}>
              {rec.status}
            </span>
            <span className="text-xs text-slate-500">
              by {rec.ratifier_role} · {new Date(rec.created_at).toLocaleString()}
            </span>
            <span className="text-xs text-slate-600">{rec.members?.length ?? 0} cases</span>
          </div>
          {rec.notes && (
            <div className="text-xs text-slate-500 mt-0.5 italic">"{rec.notes}"</div>
          )}
        </div>
        <Lock className="w-3.5 h-3.5 text-slate-600" />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && rec.members?.length > 0 && (
        <div className="border-t border-slate-800 px-5 py-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Frozen Manifest ({rec.members.length} cases) — Immutable
          </div>
          <div className="rounded-xl border border-slate-700/60 overflow-hidden">
            <div className="grid grid-cols-[1fr_90px_90px_80px] gap-2 px-4 py-2 bg-slate-800/50 text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
              <span>Case</span>
              <span className="text-center">Resolved Mark</span>
              <span className="text-center">Max</span>
              <span className="text-center">Source</span>
            </div>
            {rec.members.map((m, i) => (
              <div
                key={m.id}
                data-testid={`manifest-member-${i}`}
                className="grid grid-cols-[1fr_90px_90px_80px] gap-2 px-4 py-2.5 border-b border-slate-800/50 items-center last:border-b-0 text-xs"
              >
                <span className="text-slate-500 font-mono">{m.evaluation_case_id?.slice(0, 16)}…</span>
                <span className="text-center font-mono font-bold text-green-300">{m.resolved_mark_snapshot}</span>
                <span className="text-center text-slate-500">{m.max_mark_snapshot}</span>
                <span className="text-center text-slate-600">{m.resolved_source_snapshot}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EvaluationRatification() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [notes, setNotes] = useState("");
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);

  const MARKER_ROLES = new Set(["faculty", "student"]);
  const isMarkerOrStudent = MARKER_ROLES.has(user?.role);

  const isRatifier   = RATIFIER_ROLES.has(user?.role);
  const isCoordinator = COORD_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(
        `/evaluation/ratification/manifest?offering_id=${OFFERING_ID}&component_id=${COMPONENT_ID}`
      );
      setData(res);
    } catch (e) {
      toast.error("Failed to load ratification manifest: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Page-level role guard for marker/student roles
  if (isMarkerOrStudent) {
    return (
      <EvaluationShell title="Ratification" breadcrumb="Ratification">
        <div data-testid="ratification-access-denied" className="flex items-start gap-4 p-6 rounded-2xl border border-slate-700/60 bg-slate-900/40">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-slate-300">Restricted Access</div>
            <div className="text-xs text-slate-500 mt-1">
              The ratification workflow is accessible to Coordinators and Ratification Authorities only.
              Markers and students do not have access to this section.
            </div>
          </div>
        </div>
      </EvaluationShell>
    );
  }

  const handlePrepare = async () => {
    setSubmitting(true);
    try {
      const res = await apiFetch("/evaluation/ratification/prepare", {
        method: "POST",
        body: JSON.stringify({ offering_id: OFFERING_ID, component_id: COMPONENT_ID }),
      });
      toast.success(`${res.cases_advanced} case(s) submitted for ratification.`);
      await load();
    } catch (e) {
      if (e.status === 409) {
        toast.error("Not all cases are eligible for ratification yet: " + e.message);
      } else {
        toast.error("Preparation failed: " + e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await apiFetch(`/evaluation/ratification/${OFFERING_ID}/approve`, {
        method: "POST",
        body: JSON.stringify({
          offering_id: OFFERING_ID,
          component_id: COMPONENT_ID,
          ratifier_role: user?.role || "dean",
          scope_type: "SECTION",
          scope_id: OFFERING_ID,
          notes: notes.trim() || null,
        }),
      });
      toast.success(`Ratification approved. ${res.cases_ratified} case(s) ratified. Gradebook handoff triggered.`);
      setShowApproveConfirm(false);
      setNotes("");
      await load();
    } catch (e) {
      if (e.status === 409) {
        // Could be SOD violation
        const msg = e.message || String(e.detail || "");
        if (msg.includes("ratifier") || msg.includes("SOD") || msg.includes("conflict")) {
          toast.error("SOD Conflict: You cannot ratify a batch you participated in as a marker or moderator.");
        } else {
          toast.error("Ratification failed (conflict): " + msg);
        }
      } else {
        toast.error("Ratification failed: " + e.message);
      }
    } finally {
      setApproving(false);
    }
  };

  const readyCases   = data?.ready_cases    ?? [];
  const records      = data?.records        ?? [];
  const eligible     = data?.eligible_cases ?? [];
  const ratifiedCases = data?.ratified_cases ?? [];
  const hasRatified  = records.some((r) => r.status === "RATIFIED");

  return (
    <EvaluationShell title="Ratification" breadcrumb="Ratification">
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading ratification manifest…</span>
          </div>
        ) : (
          <>
            {/* ── Coordinator section ── */}
            {isCoordinator && (
              <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="coordinator-section">
                <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-900/60 border-b border-slate-800">
                  <Send className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-300">Coordinator — Submit for Ratification</span>
                </div>
                <div className="p-5">
                  {eligible.length > 0 && !hasRatified ? (
                    <div className="space-y-3">
                      <div className="text-xs text-slate-400">
                        {eligible.length} case(s) in eligible states
                        {" "}(MARKED / SECOND_MARKED / MODERATED) ready to submit for ratification.
                      </div>
                      <div className="rounded-xl border border-slate-800 overflow-hidden">
                        <div className="grid grid-cols-[1fr_110px_80px_80px_120px] gap-3 px-4 py-2 bg-slate-800/50 text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
                          <span>Case</span><span>Status</span><span className="text-center">Mark</span><span className="text-center">Max</span><span className="text-right">Source</span>
                        </div>
                        {eligible.map((c, i) => <CaseRow key={c.id} c={c} index={i} />)}
                      </div>
                      <button
                        data-testid="submit-for-ratification-btn"
                        onClick={handlePrepare}
                        disabled={submitting}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-700 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                      >
                        {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Submit {eligible.length} Case{eligible.length !== 1 ? "s" : ""} for Ratification
                      </button>
                    </div>
                  ) : readyCases.length > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-amber-400">
                      <CheckCircle2 className="w-4 h-4" />
                      {readyCases.length} case(s) submitted and awaiting ratifier approval.
                    </div>
                  ) : hasRatified ? (
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <CheckCircle2 className="w-4 h-4" />
                      All cases have been ratified. Gradebook handoff triggered.
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600 italic">
                      No eligible cases found. Ensure all cases have resolved marks before submitting.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Frozen manifest (READY_FOR_RATIFICATION) ── */}
            {readyCases.length > 0 && (
              <div className="rounded-xl border border-amber-800/40 overflow-hidden" data-testid="frozen-manifest">
                <div className="flex items-center gap-2 px-5 py-3.5 bg-amber-950/20 border-b border-amber-800/40">
                  <ClipboardCheck className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-300">
                    Frozen Manifest — Awaiting Ratification ({readyCases.length} case{readyCases.length !== 1 ? "s" : ""})
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  <div className="rounded-xl border border-slate-700/60 overflow-hidden">
                    <div className="grid grid-cols-[1fr_110px_80px_80px_120px] gap-3 px-4 py-2 bg-slate-800/50 text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
                      <span>Case</span><span>Status</span><span className="text-center">Mark</span><span className="text-center">Max</span><span className="text-right">Source</span>
                    </div>
                    {readyCases.map((c, i) => <CaseRow key={c.id} c={c} index={i} />)}
                  </div>

                  {/* Ratifier approve section */}
                  {isRatifier && !hasRatified && (
                    <div className="pt-2">
                      <input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional ratification notes…"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder:text-slate-600 focus:outline-none mb-3"
                      />
                      {!showApproveConfirm ? (
                        <button
                          data-testid="approve-ratification-btn"
                          onClick={() => setShowApproveConfirm(true)}
                          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
                        >
                          <Gavel className="w-4 h-4" />
                          Approve Ratification
                        </button>
                      ) : (
                        <div className="rounded-xl border border-green-800/60 bg-green-950/20 p-4 space-y-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-sm font-semibold text-green-300">Confirm Ratification</div>
                              <div className="text-xs text-green-400/80 mt-0.5">
                                This action is <strong>permanent and irreversible</strong>. All{" "}
                                {readyCases.length} case(s) will be marked RATIFIED and the
                                gradebook handoff will be triggered automatically.
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => setShowApproveConfirm(false)}
                              className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              data-testid="confirm-ratification-btn"
                              onClick={handleApprove}
                              disabled={approving}
                              className="flex items-center gap-1.5 px-5 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
                            >
                              {approving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Gavel className="w-3 h-3" />}
                              Confirm Ratification
                            </button>
                          </div>
                        </div>
                      )}
                      {/* SOD notice */}
                      <div className="mt-3 flex items-start gap-2 text-[10px] text-slate-600 bg-slate-900/40 border border-slate-800/60 rounded-lg px-3 py-2.5">
                        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-slate-500" />
                        <span>
                          <strong className="text-slate-500">SOD Enforcement:</strong> You cannot ratify a batch you participated in as
                          a marker or moderator. If you have a conflict, the server will reject this action with a conflict error.
                        </span>
                      </div>
                    </div>
                  )}
                  {!isRatifier && (
                    <div className="text-xs text-slate-600 italic">
                      Only users with ratifier authority (Dean, Institution Admin) may approve ratification.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Ratification records ── */}
            {records.length > 0 && (
              <div data-testid="ratification-records-section">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Ratification History ({records.length} record{records.length !== 1 ? "s" : ""})
                </div>
                <div className="space-y-2">
                  {records.map((rec) => (
                    <RatificationRecordCard key={rec.id} rec={rec} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Ratified cases ── */}
            {ratifiedCases.length > 0 && records.length === 0 && (
              <div className="flex items-center gap-3 p-5 rounded-xl border border-green-800/40 bg-green-950/10">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-green-300">
                    {ratifiedCases.length} Case{ratifiedCases.length !== 1 ? "s" : ""} Ratified
                  </div>
                  <div className="text-xs text-green-500/80 mt-0.5">
                    All resolved marks have been ratified. Gradebook handoff complete.
                  </div>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && eligible.length === 0 && readyCases.length === 0 && ratifiedCases.length === 0 && records.length === 0 && (
              <div
                data-testid="empty-ratification"
                className="flex flex-col items-center justify-center py-16 text-slate-500"
              >
                <Gavel className="w-10 h-10 mb-3 opacity-30" />
                <div className="text-sm font-medium">No cases available for ratification</div>
                <div className="text-xs mt-1 text-slate-600">
                  Cases appear here after marking and any required moderation is complete.
                </div>
              </div>
            )}

            {/* Refresh */}
            <div className="flex justify-end">
              <button
                data-testid="refresh-ratification-btn"
                onClick={load}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 text-xs hover:border-slate-600 hover:text-slate-200 disabled:opacity-40 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </EvaluationShell>
  );
}
